// The Queuing flow in Qmatic's three columns: services → segmentation lanes, each line
// ending in its queue → work profiles. A queue is drawn at the end of every line that
// reaches it; one no line reaches stands in the unrouted list.
import type { OperationDoc } from './types'

// 'g:<groupId>' service group · 'r:<segmentId>' rule · 'd:<groupId>' default line ·
// 'b:<line>' a line's queue bar · 'b:q:<queueId>' an unrouted queue's bar · 'w:<id>' work profile
export type NodeId = string
export interface Edge { id: string; from: NodeId; to: NodeId; kind: 'feeds' | 'route' | 'default' | 'calls' }
export interface Line { node: NodeId; bar: NodeId; kind: 'rule' | 'default'; segmentId?: string; queueId: string }
export interface Lane { groupId: string; node: NodeId; lines: Line[] }
export interface Bar { node: NodeId; queueId: string }
export interface Flow { lanes: Lane[]; unrouted: Bar[]; work: NodeId[]; edges: Edge[] }

const edge = (from: NodeId, to: NodeId, kind: Edge['kind']): Edge => ({ id: `${from}>${to}`, from, to, kind })
const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length

export function buildFlow(doc: OperationDoc): Flow {
  const known = new Set(doc.queues.map((q) => q.id))
  const edges: Edge[] = []
  const bars: Bar[] = []

  const lanes: Lane[] = doc.groups.map((g) => {
    const node = `g:${g.id}`
    const lines: Line[] = [
      ...g.segments.map((s): Line => ({ node: `r:${s.id}`, bar: `b:r:${s.id}`, kind: 'rule', segmentId: s.id, queueId: s.queueId })),
      { node: `d:${g.id}`, bar: `b:d:${g.id}`, kind: 'default', queueId: g.defaultQueueId },
    ]
    for (const l of lines) {
      edges.push(edge(node, l.node, 'feeds'))
      // A line to a queue the document lacks keeps its place, with no bar.
      if (!known.has(l.queueId)) continue
      edges.push(edge(l.node, l.bar, l.kind === 'rule' ? 'route' : 'default'))
      bars.push({ node: l.bar, queueId: l.queueId })
    }
    return { groupId: g.id, node, lines }
  })

  const reached = new Set(bars.map((b) => b.queueId))
  const unrouted = [...doc.queues]
    .filter((q) => !reached.has(q.id))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
    .map((q): Bar => ({ node: `b:q:${q.id}`, queueId: q.id }))
  bars.push(...unrouted)

  // A work profile sits level with the bars it calls from, top to bottom.
  const rank = new Map<string, number>()
  bars.forEach((b, i) => { if (!rank.has(b.queueId)) rank.set(b.queueId, i) })
  const work = doc.workProfiles
    .map((w) => {
      const ranks = [...new Set(w.queueIds)].flatMap((id) => (rank.has(id) ? [rank.get(id)!] : []))
      return { w, rank: ranks.length ? mean(ranks) : Infinity }
    })
    .sort((a, b) => (a.rank === b.rank ? 0 : a.rank - b.rank) || a.w.name.localeCompare(b.w.name))
  for (const { w } of work) {
    const calls = new Set(w.queueIds)
    for (const b of bars) if (calls.has(b.queueId)) edges.push(edge(b.node, `w:${w.id}`, 'calls'))
  }

  return { lanes, unrouted, work: work.map(({ w }) => `w:${w.id}`), edges }
}

/** Everything the node draws from and everything it feeds, never its siblings. */
export function highlight(flow: Flow, node: NodeId): { nodes: Set<NodeId>; edges: Set<string> } {
  const nodes = new Set<NodeId>([node])
  const lit = new Set<string>()
  const walk = (down: boolean) => {
    const stack = [node]
    while (stack.length) {
      const n = stack.pop()!
      for (const e of flow.edges) {
        if (down ? e.from !== n : e.to !== n) continue
        lit.add(e.id)
        const next = down ? e.to : e.from
        if (!nodes.has(next)) { nodes.add(next); stack.push(next) }
      }
    }
  }
  walk(false)
  walk(true)
  return { nodes, edges: lit }
}

const num = (n: number) => String(Math.round(n * 10) / 10)

/** A horizontal S-curve from the right edge of one box to the left edge of the next. */
export function connectorPath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const mid = num((from.x + to.x) / 2)
  return `M${num(from.x)} ${num(from.y)} C${mid} ${num(from.y)} ${mid} ${num(to.y)} ${num(to.x)} ${num(to.y)}`
}
